import { TFormFieldContactPreferences } from 'js/components/common/Form/types';
import { ISetDepositLimit } from 'js/hooks/socket/hooks/useSetDepositLimits/types';
import { IBetLimit, ISetBetLimitsRequestData } from 'js/hooks/socket/hooks/useSetBetLimits/types';
import { IContactPreferenceItem } from 'js/hooks/socket/hooks/useSetPlayerContactPreferences/types';
import { ILossLimit, ISetLossLimitsRequestData } from 'js/hooks/socket/hooks/useSetLossLimits/types';

import * as Types from './types';
import { fieldToImsValue, getExtraImsFields } from '../../helpers';
import {
    ADDRESS_DETAILS_KEYS,
    BET_LIMITS_MAP,
    CONTACT_PREFERENCES_FIELD_TYPE,
    DEPOSIT_LIMITS_MAP,
    LOSS_LIMITS_MAP
} from '../../constants';

export const getImsFieldsData = (
    { fields, allowedCountries, formData }: Types.IGetFieldsDataOptions
): Record<string, string> => {
    return fields
        .reduce((accumulator, field) => {
            const value = fieldToImsValue(formData, field, allowedCountries);
            const extraImsFields = getExtraImsFields(field.extraImsFields, value);

            return {
                ...accumulator,
                ...extraImsFields,
                ...field.imsField && { [field.imsField]: value }
            };
        }, {});
};

export const getDepositLimitsData = (formData: Record<string, string>): ISetDepositLimit[] => {
    return Object.keys(DEPOSIT_LIMITS_MAP).reduce((acc, limit) => {
        if (Number(formData[limit])) {
            acc.push({
                amount: formData[limit],
                timePeriod: DEPOSIT_LIMITS_MAP[limit]
            });
        }

        return acc;
    }, [] as ISetDepositLimit[]);
};

export const getBetLimitsData = (formData: Record<string, string>): ISetBetLimitsRequestData => {
    const limits = Object.keys(BET_LIMITS_MAP).reduce((acc, limit) => {
        if (Number(formData[limit])) {
            acc.push({
                amount: formData[limit],
                timePeriod: BET_LIMITS_MAP[limit]
            });
        }

        return acc;
    }, [] as IBetLimit[]);

    return {
        limits,
        action: formData.betlimitaction || 'message'
    };
};

export const getLossLimitsData = (formData: Record<string, string>): ISetLossLimitsRequestData => {
    const limits = Object.keys(LOSS_LIMITS_MAP).reduce((acc, limit) => {
        if (Number(formData[limit])) {
            acc.push({
                amount: formData[limit],
                timePeriod: LOSS_LIMITS_MAP[limit]
            });
        }

        return acc;
    }, [] as ILossLimit[]);

    return {
        limits,
        action: formData.losslimitaction || 'message'
    };
};

export const getDataWithoutAddressFields = (imsFields: Record<string, string>): Record<string, string> => {
    return Object.keys(imsFields)
        .filter((key) => !ADDRESS_DETAILS_KEYS.includes(key))
        .reduce((accumulator, key) => ({
            ...accumulator,
            [key]: imsFields[key]
        }), {});
};

export const getChangedFieldsData = (
    source: Record<string, string>, target: Record<string, string>
): Record<string, string> => Object.fromEntries(
    Object.entries({ ...source, ...target })
    .filter(([key, value]) => !Object.is(source[key], value))
);

export const getContactPreferencesData = (
    fields: TFormFieldContactPreferences[], formData: Record<string, boolean>, getFieldId: (id: string) => string
): IContactPreferenceItem[] => {
    return fields.reduce((acc, field) => {
        const {
            contactChannelName,
            contactChannelType,
            contactPreferences,
            enableReverseValue = false,
            id,
            name,
            preferencesType
        } = field;
        const fieldId = `${getFieldId(id)}_${name}`;

        if (preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.THIRD_PARTY) {
            return [...acc, { type: preferencesType, preference: formData[fieldId] }];
        }

        if (preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.GENERAL && contactChannelName && contactChannelType) {
            const data = contactChannelName.split(',').map(channel => ({
                channel: channel.trim(),
                preference: !enableReverseValue ? formData[fieldId] : !formData[fieldId],
                type: contactChannelType === CONTACT_PREFERENCES_FIELD_TYPE.NON_PROMOTIONAL
                    ? CONTACT_PREFERENCES_FIELD_TYPE.ACCOUNT
                    : contactChannelType
            }));

            return [...acc, ...data];
        }

        if (Array.isArray(contactPreferences)) {
            const data = contactPreferences.map(pref => {
                if (pref?.products?.length) {
                    return pref.products.map(product => ({
                        channel: pref.channel,
                        product,
                        preference: formData[`${fieldId}-${pref.channel}-${product}`],
                        type: preferencesType
                    }));
                }

                return {
                    channel: pref.channel,
                    preference: !enableReverseValue ? formData[`${fieldId}-${pref.channel}`] : !formData[`${fieldId}-${pref.channel}`],
                    type: preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.NON_PROMOTIONAL
                        ? CONTACT_PREFERENCES_FIELD_TYPE.ACCOUNT
                        : preferencesType
                };
            });

            return [...acc, ...data.flat()];
        }

        return acc;
    }, [] as IContactPreferenceItem[]);
};
